import * as React from "react";
import { useEffect, useState } from "react";

import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../Common/ModelPath/ModelPath";

import { createHocDisplayName } from "./TypingUtils";
import { useFormObject } from "./FormObjectProvider";
import {
    HighlightedControlWithFocus,
    HighlightingOptions,
    HighlightingType,
    resolveHighlightingOptions,
} from "./ExternalHighlighting";

export interface WithHighlightedInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    highlighted: boolean;
    highlightingType?: HighlightingType;
    highlightingTooltipText?: string;
    isTooltipFixed?: boolean;
    onTooltipClose?: () => void;
}

export function withHighlighting<P>(Component: React.ComponentType<P>): React.ComponentClass<Omit<P, "highlighted">>;
export function withHighlighting<P extends WithHighlightedInjectableProps>(
    WrappedComponent: React.ComponentType<P>
): React.ComponentType<P> {
    function WithHighlighting(props: P): JSX.Element {
        const form = useFormObject();
        const [highlighted, setHighlighted] = useState(false);
        const [externalHighlighted, setExternalHighlighted] = useState(false);
        const [externalFocused, setExternalFocused] = useState<boolean>(false);
        const [externalHighlightingOptions, setExternalHighlightingOptions] = useState<HighlightingOptions>();

        useEffect(() => {
            const checkHighlighted = (paths: AbsoluteModelPath[]) => {
                const isHighlighted = paths.some(path => path.isIncludes(props.bindingPath));
                setHighlighted(isHighlighted);
            };

            const unsubscriber = form.onDependencyPathsFromHoveredPathChanged(checkHighlighted);
            return () => {
                unsubscriber();
            };
        }, [props.bindingPath]);

        useEffect(() => {
            const checkExternalHighlighted = (highlightedControls: HighlightedControlWithFocus[]) => {
                const isExternalHighlighted = highlightedControls.some(options =>
                    options.pathMask.isIncludes(props.bindingPath)
                );
                const isFocusChanged = highlightedControls.some(options => options.focused);
                const highlightedOptions = highlightedControls.find(path =>
                    path.pathMask.isIncludes(props.bindingPath)
                );

                if (isFocusChanged) {
                    setExternalFocused(highlightedOptions?.focused ?? false);
                    return;
                }
                setExternalHighlighted(isExternalHighlighted);
                if (isExternalHighlighted) {
                    setExternalHighlightingOptions({
                        highlightingType: highlightedOptions?.highlightingType,
                        tooltipText: highlightedOptions?.tooltipText,
                        isTooltipFixed: highlightedOptions?.isTooltipFixed,
                    });
                } else {
                    setExternalHighlightingOptions(undefined);
                }
            };

            const unsubscriber = form.onExternalHighlightPaths(checkExternalHighlighted);
            return () => {
                unsubscriber();
            };
        }, [props.bindingPath]);

        return (
            <WrappedComponent
                {...props}
                highlighted={highlighted}
                externalHighlighted={externalHighlighted}
                externalFocused={externalFocused}
                externalHighlightingOptions={resolveHighlightingOptions(externalHighlightingOptions, externalFocused)}
            />
        );
    }
    WithHighlighting.displayName = createHocDisplayName(WrappedComponent, "Highlighting");
    return WithHighlighting;
}
